import { Fail } from './types';
import { ItemList } from '../types/main';
import { Item, ItemId } from '../types/items';
import { Notification, NotificationType } from './modules/notifications/types';
import { urlsForRedirect } from '../utility/constants';

/* eslint-disable no-param-reassign, no-plusplus, @typescript-eslint/no-explicit-any, no-nested-ternary, no-confusing-arrow, arrow-body-style */

type ResponseErrorType = {
  response: {
    data: {
      dev_error: string;
      error: string;
      error_code: string;
    };
    status: number;
  };
  message: string;
};

interface SortOption <T extends Item>{
  field: keyof T;
  rule: 'asc' | 'desc';
}

export const getErrorMessage = (error: ResponseErrorType): Fail => ({
  message: error.response?.data?.error || 'An Error Occurred',
});

export const getErrorNotification = (error: ResponseErrorType, type: NotificationType = 'error'): Notification => ({
  message: error.response?.data?.error || 'An Error Occurred',
  type,
});

export const getError = (error: ResponseErrorType): string => error.response.data.error;

export const addItemToState = <T extends Item>(
  state: ItemList<T> | null, payload: T, position: 'start' | 'end' = 'start',
) => {
  if (state === null) {
    return state;
  }
  if (state.pagination.pageSize <= state.items.length) {
    state.items.pop();
  }
  state.pagination.totalItems++;
  if (position === 'start') {
    state.items = [payload, ...state.items];
  } else {
    state.items = [...state.items, payload];
  }

  return { ...state };
};

export const removeItemByState = <T extends Item>(state: ItemList<T> | null, payload: ItemId) => {
  if (state !== null) {
    const index = state.items.findIndex((item) => item.id === payload);
    if (index !== -1) {
      state.items.splice(index, 1);
      state.pagination.totalItems--;
    }
    return { ...state };
  }
  return state;
};

export const updateItemByState = <T extends Item>(state: ItemList<T> | null, payload: T) => {
  if (state === null) {
    return state;
  }
  const index = state.items.findIndex((item) => item.id === payload.id);
  if (index === -1) {
    return addItemToState(state, payload);
  }
  state.items[index] = payload;
  return { ...state };
};

export const updateItemListByState = <T extends Item>(
  state: ItemList<T> | null, payload: T[],
) => (state === null ? state : { ...state, items: [...payload] });

export const getQueryParamsByItemList = (data: ItemList<any>) => {
  return {
    page: data.pagination.page,
    page_size: data.pagination.pageSize,
    sort_key: data.sorting.key,
    sort_dir: data.sorting.dir,
    filter: Object.fromEntries(
      data.filters.active.map(({ key, value }) => [key, value]),
    ),
  };
};

export const sortState = <T extends Item>(state: ItemList<T> | null, options: SortOption<T>) => {
  if (state === null) {
    return state;
  }
  let A = -1;
  let B = 1;
  if (options.rule === 'desc') {
    A = 1;
    B = -1;
  }
  state.items.sort((a, b) => {
    return a[options.field] !== b[options.field] ? a[options.field] < b[options.field] ? A : B : 0;
  });
  return state;
};

export const checkUrlsForRedirect = (currentUrl: string): boolean => {
  let needRedirect = false;

  if (currentUrl.indexOf('unauthorized') !== -1) {
    return needRedirect;
  }

  urlsForRedirect.forEach((url) => {
    if (currentUrl.indexOf(url) !== -1) {
      needRedirect = true;
    }
  });

  // except urls
  [
    'payment-method',
  ]
    .forEach((url) => {
      if (currentUrl.indexOf(url) !== -1) {
        needRedirect = false;
      }
    });

  return needRedirect;
};
